import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import TopicHeader from './TopicHeader';
import ConceptList from './ConceptList';
import AddConceptForm from './AddConceptForm';
import { supabase } from '../../supabaseClient';
import TopicActions from './TopicActions';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { changeTopic, updateTopic, updateConceptImportance, moveBasketToTop, topicConceptsSynced } from '../../store/workspacesSlice';
import OfflineStatusBanner from '../OfflineStatusBanner/OfflineStatusBanner';
import BasketSelectionInterface from '../BasketSelectionInterface/BasketSelectionInterface';

// Utility function to determine text color based on background color
// Returns black (#000000) for light backgrounds and white (#FFFFFF) for dark backgrounds
function getContrastColor(hexColor) {
  // Convert hex to RGB values
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

const TopicDetail = () => {
  const location = useLocation();
  const { topicId } = useParams();
  const navigate = useNavigate();
  const topicData = location.state?.topic;
  const basketData = location.state?.basket;
  const workspaceIdFromState = location.state?.workspaceId;
  console.log('Location state:', location.state);

  const [topic, setTopic] = useState(topicData || {
    id: topicId,
    name: '',
    basket_id: '',
    workspace_id: workspaceIdFromState || '',
    concepts: [],
    tag: ''
  });

  const [basket, setBasket] = useState(basketData || null);
  const [associatedTopics, setAssociatedTopics] = useState([]);
  const [workspaceName, setWorkspaceName] = useState('');
  const dispatch = useDispatch();
  const currentWorkspace = useSelector(state => state.workspaces.currentWorkspace);
  // eslint-disable-next-line no-unused-vars
  const [topicChanged, setTopicChanged] = useState(false);
  const [showBasketSelection, setShowBasketSelection] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const titleInputRef = useRef(null);
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
  const [nudgeState, setNudgeState] = useState('idle');
  const [nudgeStorageKey, setNudgeStorageKey] = useState('topic_hard_nudge_seen_anonymous');

  // Push concept edits into the workspace cache so Connections shows them instantly on return.
  useEffect(() => {
    if (!topicChanged || !topic?.id) return;
    dispatch(topicConceptsSynced({ topicId: topic.id, concepts: topic.concepts }));
  }, [topicChanged, topic?.id, topic?.concepts, dispatch]);

  // Fetch topic data and associated topics when component mounts or topicId changes
  useEffect(() => {
    const fetchTopicData = async () => {
      try {
        // Fetch topic data with related entities using Supabase join
        const { data: topicData, error: topicError } = await supabase
          .from('topics')
          .select(`
            *,
            concepts (*),
            baskets (name),
            tags (
              id,
              name,
              color
            ),
            workspaces (id, name)
          `)
          .eq('id', topicId)
          .single();

        if (topicError) throw topicError;

        console.log('Fetched topic data:', topicData);

        // Update state with fetched data
        setTopic(prevTopic => ({
          ...prevTopic,
          ...topicData,
          workspace_id: workspaceIdFromState || topicData?.workspace_id || prevTopic.workspace_id,
          concepts: topicData?.concepts || [],
          tags: topicData?.tags || []
        }));

        setBasket(topicData?.baskets || null);
        setWorkspaceName(topicData?.workspaces?.name || '');

        // Fetch associated topics if basket_id is available
        if (topicData?.basket_id) {
          const { data: associatedTopicsData, error: associatedTopicsError } = await supabase
            .from('topics')
            .select('id, name, relation_description')
            .eq('basket_id', topicData.basket_id)
            .neq('id', topicId);

          if (associatedTopicsError) throw associatedTopicsError;

          console.log('Fetched associated topics:', associatedTopicsData);

          setAssociatedTopics(associatedTopicsData || []);
        } else {
          console.log('No basket_id found for the topic');
          setAssociatedTopics([]);
        }
      } catch (error) {
        console.error('Error fetching topic data:', error);
      }
    };

    fetchTopicData();
  }, [topicId, workspaceIdFromState]);

  // Initialize one-time nudge state per user
  useEffect(() => {
    let isMounted = true;

    const initializeNudgeState = async () => {
      try {
        const { data, error } = await supabase.auth.getUser();
        if (error) throw error;

        const userId = data?.user?.id || 'anonymous';
        const storageKey = `topic_hard_nudge_seen_${userId}`;
        if (!isMounted) return;

        setNudgeStorageKey(storageKey);

        if (typeof window !== 'undefined') {
          const hasSeenNudge = localStorage.getItem(storageKey) === 'true';
          setNudgeState(hasSeenNudge ? 'dismissed' : 'idle');
        }
      } catch (error) {
        console.error('Error initializing hard nudge state:', error);
        if (typeof window !== 'undefined') {
          const hasSeenNudge = localStorage.getItem('topic_hard_nudge_seen_anonymous') === 'true';
          setNudgeState(hasSeenNudge ? 'dismissed' : 'idle');
        }
      }
    };

    initializeNudgeState();

    return () => {
      isMounted = false;
    };
  }, []);

  // Handler for adding a new concept to the topic
  const handleAddConcept = async (newConceptText) => {
    try {
      // Insert new concept into database with proper order index
      const { data, error } = await supabase
        .from('concepts')
        .insert({
          text: newConceptText,
          topic_id: topicId,
          is_highlighted: false,
          order_index: topic.concepts.length
        })
        .select()
        .single();

      if (error) throw error;

      setTopic(prevTopic => ({
        ...prevTopic,
        concepts: [...prevTopic.concepts, data]
      }));

      toast.success('Concept added successfully');
      setTopicChanged(true);
      return Promise.resolve();
    } catch (error) {
      console.error('Error adding concept:', error);
      return Promise.reject(error);
    }
  };

  // Handler for editing an existing concept
  const handleEditConcept = async (conceptId, newText) => {
    try {
      // Update concept text and timestamp in database
      const { error } = await supabase
        .from('concepts')
        .update({ text: newText, updated_at: new Date() })
        .eq('id', conceptId);

      if (error) throw error;

      setTopic(prevTopic => ({
        ...prevTopic,
        concepts: prevTopic.concepts.map(c => 
          c.id === conceptId ? {...c, text: newText} : c
        )
      }));
      setTopicChanged(true);
      return Promise.resolve();
    } catch (error) {
      console.error('Error updating concept:', error);
      return Promise.reject(error);
    }
  };

  // Handler for deleting a concept
  const handleDeleteConcept = async (conceptId) => {
    try {
      const { error } = await supabase
        .from('concepts')
        .delete()
        .eq('id', conceptId);

      if (error) throw error;

      setTopic(prevTopic => ({
        ...prevTopic,
        concepts: prevTopic.concepts.filter(c => c.id !== conceptId)
      }));

      // Remove success toast since we're showing the undo toast instead
      setTopicChanged(true);
    } catch (error) {
      console.error('Error deleting concept:', error);
      toast.error('Failed to delete concept: ' + error.message);
    }
  };

  // Handler for toggling "mark as hard" concept state
  const handleMarkAsHard = async (conceptId) => {
    if (nudgeState === 'idle') {
      setNudgeState('visible');
      if (typeof window !== 'undefined') {
        localStorage.setItem(nudgeStorageKey, 'true');
      }
      return;
    }

    try {
      const conceptToUpdate = topic.concepts.find(c => c.id === conceptId);
      if (!conceptToUpdate) throw new Error('Concept not found');

      const newHighlightedState = !conceptToUpdate.is_highlighted;

      await dispatch(updateConceptImportance({
        conceptId,
        topicId: topic.id,
        newHighlightedState
      })).unwrap();

      // Update local state
      setTopic(prevTopic => ({
        ...prevTopic,
        concepts: prevTopic.concepts.map(c => 
          c.id === conceptId 
            ? {...c, is_highlighted: newHighlightedState} 
            : {...c, is_highlighted: false}
        )
      }));

      toast.success(newHighlightedState ? 'Marked as hard' : 'Unmarked as hard');
      setTopicChanged(true);
    } catch (error) {
      console.error('Error updating concept importance:', error);
      toast.error('Failed to update concept importance: ' + error.message);
    }
  };

  const handleDismissNudge = () => {
    setNudgeState('dismissed');
    if (typeof window !== 'undefined') {
      localStorage.setItem(nudgeStorageKey, 'true');
    }
  };

  // Modified handler for navigating back
  const handleBackNavigation = () => {
    // Original navigation logic
    console.log('Handling back navigation:', location.state?.fromBasket);
    console.log('Basket ID:', location?.state?.basket?.id);
    console.log('Workspace ID:', topic.workspace_id);
    console.log('Workspace Name:', workspaceName);

    if (location.state?.fromBasket && location?.state?.basket?.id) {
      navigate(`/basket/${location.state.basket.id}`, { 
        state: { 
          basketId: location.state.basket.id,
          basket: location.state.basket,
          workspaceId: topic.workspace_id,
          workspaceName: workspaceName,
          searchTerm: location.state?.searchTerm
        }
      });
    } else {
      navigate(`/workspace/${topic.workspace_id}`, {
        state: { 
          activeTab: 'Items',
          shouldRefetch: true,
          lastFetchedAt: Date.now(),
          workspaceName: workspaceName,
          searchTerm: location.state?.searchTerm
        }
      });
    }
  };

  // Handler for changing the basket of a topic
  const handleBasketChange = async (newBasketId) => {
    try {
      if (!topic.workspace_id) {
        throw new Error('Workspace ID is missing');
      }

      await dispatch(changeTopic({ 
        topicId: topic.id, 
        newBasketId, 
        workspaceId: topic.workspace_id
      })).unwrap();
      
      // Move the basket to top
      dispatch(moveBasketToTop({ basketId: newBasketId }));

      // Update local state
      setTopic(prevTopic => ({
        ...prevTopic,
        basket_id: newBasketId
      }));

      const newBasket = currentWorkspace.baskets.find(b => b.id === newBasketId);
      setBasket(newBasket || null);

      toast.success('Topic moved to new basket successfully');
      setTopicChanged(true);

      // Navigate to the new basket if it was just created
      if (location.state?.isNewBasket) {
        navigate(`/basket/${newBasketId}`, {
          state: {
            basket: newBasket,
            workspaceId: topic.workspace_id,
            fromWorkspace: true
          }
        });
      }
    } catch (error) {
      console.error('Error changing basket:', error);
      toast.error('Failed to move topic: ' + error.message);
      throw error;
    }
  };

  const handleMenuToggle = () => {
    document.getElementById('topicMenu').classList.toggle('hidden');
  };

  const handleBasketSelected = async (newBasketId) => {
    const hasHard = (topic.concepts || []).some((c) => c.is_highlighted);
    if (!hasHard) {
      toast.error('Mark at least one detail as hard before changing baskets');
      setShowBasketSelection(false);
      return;
    }
    try {
      await handleBasketChange(newBasketId);
      setShowBasketSelection(false);
    } catch (error) {
      console.error('Error handling basket selection:', error);
      toast.error('Failed to move topic to new basket');
    }
  };

  // Handler for saving topic title changes
  const handleSaveTopicTitle = useCallback(async () => {
    // Skip update if title hasn't changed
    if (editedTitle.trim() === topic.name) {
      setIsEditingTitle(false);
      return;
    }

    try {
      // Prepare update data with new title and timestamp
      const updates = {
        name: editedTitle.trim(),
        updated_at: new Date().toISOString()
      };

      // Dispatch update action to Redux store
      await dispatch(updateTopic({
        topicId: topic.id,
        updates,
        workspaceId: topic.workspace_id
      })).unwrap();

      setTopic(prevTopic => ({
        ...prevTopic,
        name: editedTitle.trim()
      }));
      
      setIsEditingTitle(false);
      toast.success('Topic name updated successfully');
    } catch (error) {
      console.error('Error updating topic name:', error);
      toast.error('Failed to update topic name: ' + error.message);
      setEditedTitle(topic.name); // Reset to original name on error
    }
  }, [editedTitle, topic.name, topic.id, topic.workspace_id, dispatch]);

  // Effect to handle clicks outside title edit input
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isEditingTitle && titleInputRef.current && !titleInputRef.current.contains(event.target)) {
        handleSaveTopicTitle();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditingTitle, editedTitle, handleSaveTopicTitle]);

  // Effect to focus and select title input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Add this handler function inside the TopicDetail component
  const handleDescriptionUpdate = async (newDescription) => {
    try {
      const { error } = await supabase
        .from('topics')
        .update({ 
          relation_description: newDescription,
          updated_at: new Date().toISOString()
        })
        .eq('id', topic.id)
        .select()
        .single();

      if (error) throw error;

      setTopic(prevTopic => ({
        ...prevTopic,
        relation_description: newDescription
      }));

      toast.success('Description updated successfully');
    } catch (error) {
      console.error('Error updating description:', error);
      toast.error('Failed to update description');
    }
  };

  const hasHardConcept = (topic.concepts || []).some((c) => c.is_highlighted);
  const isBasketChangeBlocked = !hasHardConcept;

  return (
    <div className="min-h-screen bg-neutral-50 pb-24">
      <TopicHeader 
        basket={basket?.name}
        basketDescription={topic.relation_description}
        associatedTopics={associatedTopics}
        topic={topic}
        onBack={handleBackNavigation}
        onMenuToggle={handleMenuToggle}
        onDescriptionUpdate={handleDescriptionUpdate}
      />

      <OfflineStatusBanner />

      <main className="max-w-6xl mx-auto px-4 py-4 md:py-8 space-y-4 md:space-y-6">
        <div className="bg-white rounded-xl border-2 border-neutral-900 p-4 md:p-6">
          <div className="mb-4 md:mb-6">
            {isEditingTitle ? (
              <input
                ref={titleInputRef}
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveTopicTitle();
                  } else if (e.key === 'Escape') {
                    setIsEditingTitle(false);
                    setEditedTitle(topic.name);
                  }
                }}
                className="text-xl md:text-2xl font-bold text-neutral-900 w-full bg-transparent border-b-2 border-neutral-900 focus:outline-none focus:border-cranberry-900 px-1 py-0.5"
              />
            ) : (
              <h2 
                className="text-xl md:text-2xl font-bold text-neutral-900 cursor-pointer hover:bg-neutral-50 px-1 py-0.5 rounded transition-colors"
                onClick={() => {
                  setIsEditingTitle(true);
                  setEditedTitle(topic.name);
                }}
              >
                {topic.name}
              </h2>
            )}
            {topic.tags && (
              <span 
                key={topic.tags.id} 
                className="inline-block px-3 py-1 text-sm rounded-full mt-2 border border-neutral-900"
                style={{
                  backgroundColor: topic.tags.color || '#981F2B',
                  color: topic.tags.color ? getContrastColor(topic.tags.color) : '#FFFFFF'
                }}
              >
                {topic.tags.name}
              </span>
            )}
          </div>

          <div className="space-y-4 md:space-y-6">
            {topic.concepts && topic.concepts.length > 0 ? (
              <ConceptList 
                concepts={topic.concepts}
                onEdit={handleEditConcept}
                onDelete={handleDeleteConcept}
                onMarkAsHard={handleMarkAsHard}
                nudgeState={nudgeState}
                onDismissNudge={handleDismissNudge}
              />
            ) : (
              <p className="text-neutral-600">No concepts added yet.</p>
            )}
            <AddConceptForm onAddConcept={handleAddConcept} />
          </div>
        </div>
      </main>

      <div id="topicMenu" className="fixed right-4 top-16 w-48 bg-white rounded-xl border-2 border-neutral-900 hidden z-10">
        <div className="py-1">
          <button className="block w-full px-4 py-2 text-sm text-neutral-900 hover:bg-neutral-50 text-left transition-colors">
            Edit Topic
          </button>
          <button className="block w-full px-4 py-2 text-sm text-neutral-900 hover:bg-neutral-50 text-left transition-colors">
            Delete Topic
          </button>
          <button className="block w-full px-4 py-2 text-sm text-neutral-900 hover:bg-neutral-50 text-left transition-colors">
            Move to Another Basket
          </button>
        </div>
      </div>

      <TopicActions 
        isInBasket={!!topic.basket_id} 
        onBasketChange={() => setShowBasketSelection(true)}
        currentBasketId={topic.basket_id}
        topic={topic}
        baskets={currentWorkspace.baskets}
        workspaceId={topic.workspace_id || workspaceIdFromState}
      />

      {showBasketSelection && (
        <BasketSelectionInterface
          onClose={() => setShowBasketSelection(false)}
          onBasketSelected={handleBasketSelected}
          currentBasketId={topic.basket_id}
          topic={topic}
          workspaceId={topic.workspace_id || workspaceIdFromState}
        />
      )}

      {showLeaveConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-xl border-2 border-neutral-900 shadow-xl p-6 w-full max-w-md mx-4 relative">
            <button 
              onClick={() => setShowLeaveConfirmation(false)}
              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-neutral-100 transition-colors"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            
            <h2 className="text-xl font-bold text-neutral-900 mb-3">Oops! This topic missed the basket.</h2>
            <p className="text-neutral-700 mb-6">
              Don't let it slip away—add it now to keep it in your rotation. You'll see it regularly when looking for baskets, reinforcing your memory. Even if it's alone for now, placing it in a basket ensures it won't be forgotten.
            </p>
            {isBasketChangeBlocked && (
              <p className="text-sm text-neutral-600 mb-4" role="status">
                Mark at least one detail as hard before you can add this topic to a basket or move it between baskets.
              </p>
            )}
            <div className="flex flex-col space-y-4">
              <button
                type="button"
                onClick={() => {
                  if (isBasketChangeBlocked) return;
                  setShowLeaveConfirmation(false);
                  setShowBasketSelection(true);
                }}
                disabled={isBasketChangeBlocked}
                aria-disabled={isBasketChangeBlocked}
                title={isBasketChangeBlocked ? 'Mark at least one detail as hard first' : undefined}
                className={`
                  w-full py-3 rounded-xl font-medium transition-colors
                  ${isBasketChangeBlocked
                    ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed'
                    : 'bg-cranberry-700 text-white hover:bg-cranberry-600'}
                `}
              >
                Add to a Basket
              </button>
              <button
                onClick={handleBackNavigation}
                className="text-neutral-500 hover:text-neutral-700 text-sm font-medium py-2 transition-colors text-center"
              >
                Leave Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopicDetail;
